import React, { useState } from "react";
import { Play } from "lucide-react";

const POSTER_URL = (videoId) => `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
const FALLBACK_POSTER_URL = (videoId) => `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
const EMBED_URL = (videoId) => `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;

/**
 * Click-to-play YouTube player.
 *
 * Default state: full-bleed YouTube thumbnail with a centered play button
 * and zero YouTube chrome (no title bar, no "Watch on YouTube" overlay).
 * On click: swaps to the YouTube iframe with autoplay; chrome appears then,
 * which is acceptable because the user has already engaged.
 *
 * Side benefits:
 *   - Iframe doesn't mount until interaction → faster initial page load,
 *     better Lighthouse score, no preconnect cost to youtube.com on load.
 *   - YouTube's `maxresdefault.jpg` (1280×720) sometimes 404s for newer
 *     videos until processing finishes — we automatically fall back to
 *     `hqdefault.jpg` (480×360) so the player never shows broken state.
 *
 * Fills 100% of its parent container. The parent MUST establish a sized,
 * `position: relative` (or `aspect-video relative`) layout context — same
 * contract the raw iframe had.
 */
export default function YouTubePoster({ videoId, title = "Video", posterSrc, className = "" }) {
  const [playing, setPlaying] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);

  if (playing) {
    return (
      <iframe
        width="100%"
        height="100%"
        className={`absolute inset-0 w-full h-full ${className}`}
        src={EMBED_URL(videoId)}
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  const resolvedPoster = posterSrc || (posterFailed ? FALLBACK_POSTER_URL(videoId) : POSTER_URL(videoId));

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      className={`absolute inset-0 w-full h-full group focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-light ${className}`}
      aria-label={`Play ${title}`}
    >
      <img
        src={resolvedPoster}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        onError={() => setPosterFailed(true)}
      />
      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex h-20 w-20 md:h-24 md:w-24 items-center justify-center rounded-full bg-red-600 shadow-2xl shadow-black/40 group-hover:bg-red-500 group-hover:scale-105 transition-all">
          <Play className="h-9 w-9 md:h-10 md:w-10 text-white ml-1.5" fill="white" strokeWidth={0} />
        </div>
      </div>
    </button>
  );
}
